import React from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import api from "@/api/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import StatusBadge from "@/components/StatusBadge";
import { Fuel, MapPin, ArrowRight, Home } from "lucide-react";

export default function DriverTrips() {
  const { data } = useQuery({
    queryKey: ["driver-trips"],
    queryFn: () => api.get("/orders?limit=50").then((r) => r.data.items),
    refetchInterval: 15000,
  });

  // After each delivery, the app polls this endpoint to tell the driver
  // whether to continue with a pending trip or head back to base.
  const { data: nextAction } = useQuery({
    queryKey: ["driver-next-action"],
    queryFn: () => api.get("/dispatch/driver/next-action").then((r) => r.data),
    refetchInterval: 15000,
  });

  const active = (data || []).filter((o) => ["dispatched", "loading", "in_transit"].includes(o.status));
  const done = (data || []).filter((o) => o.status === "delivered").slice(0, 10);

  return (
    <div className="space-y-6" data-testid="driver-trips">
      <div>
        <h1 className="font-display text-3xl font-black tracking-tight">My Trips</h1>
        <p className="text-sm text-muted-foreground">{active.length} active · {done.length} recent</p>
      </div>

      {/* Next-action banner — surfaces after each trip ends */}
      {nextAction && active.length === 0 && (
        <Card className={nextAction.action === "next_trip" ? "border-primary/40 bg-primary/5" : "border-border/60 bg-muted/40"} data-testid="driver-next-action-card">
          <CardContent className="flex items-center justify-between gap-4 p-4">
            <div>
              <div className="text-sm font-bold">
                {nextAction.action === "next_trip"
                  ? `Next trip queued · ${nextAction.order?.code}`
                  : "Return to base"}
              </div>
              <div className="mt-1 text-xs text-muted-foreground">
                {nextAction.action === "next_trip"
                  ? `${nextAction.order?.customer_name} · ${nextAction.order?.quantity_litres}L · ${nextAction.queue_length} in queue`
                  : nextAction.reason || "No pending assignments — head back to the depot."}
              </div>
            </div>
            {nextAction.action === "next_trip" ? (
              <Link to={`/driver/trip/${nextAction.order?.id}`} className="rounded-full bg-primary px-4 py-2 text-xs font-bold text-primary-foreground" data-testid="btn-drv-start-next">
                Start next trip
              </Link>
            ) : (
              <div className="flex items-center gap-1 text-xs font-bold text-muted-foreground"><Home size={14} /> Base</div>
            )}
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader><CardTitle>Active</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          {active.length === 0 && <div className="py-6 text-center text-sm text-muted-foreground">No active trips assigned.</div>}
          {active.map((o) => (
            <Link key={o.id} to={`/driver/trip/${o.id}`} className="flex items-center justify-between rounded-lg border border-border/60 p-4 transition-transform hover:-translate-y-0.5" data-testid={`trip-${o.code}`}>
              <div>
                <div className="font-mono text-sm font-bold">{o.code}</div>
                <div className="text-sm">{o.customer_name}</div>
                <div className="mt-1 flex items-center gap-3 text-xs text-muted-foreground">
                  <span className="inline-flex items-center gap-1"><Fuel size={12} /> {o.quantity_litres}L</span>
                  <span className="inline-flex items-center gap-1"><MapPin size={12} /> {o.site_name}</span>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <StatusBadge status={o.status} />
                <ArrowRight size={16} />
              </div>
            </Link>
          ))}
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle>Completed today</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          {done.length === 0 && <div className="py-6 text-center text-sm text-muted-foreground">No completed trips.</div>}
          {done.map((o) => (
            <div key={o.id} className="flex items-center justify-between rounded-lg border border-border/40 p-3">
              <div>
                <div className="font-mono text-sm font-bold">{o.code}</div>
                <div className="text-xs text-muted-foreground">{o.customer_name} · {o.delivered_quantity || o.quantity_litres}L</div>
              </div>
              <StatusBadge status={o.status} />
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}
